import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { usePlans } from '@/hooks/usePlans';
import { useCreateWorkspace } from '@/hooks/useCreateWorkspace';

interface CreateWorkspaceDirectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (workspaceId: string) => void;
}

export function CreateWorkspaceDirectModal({ isOpen, onClose, onSuccess }: CreateWorkspaceDirectModalProps) {
  const [workspaceName, setWorkspaceName] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const { user } = useAuth();
  const { data: plansData, isLoading: plansLoading } = usePlans();
  const createWorkspaceMutation = useCreateWorkspace();

  const handleCreate = async () => {
    if (!workspaceName.trim()) {
      toast.error('Please enter a workspace name');
      return;
    }

    if (!selectedPlan) {
      toast.error('Please select a plan');
      return;
    }

    if (!user) {
      toast.error('User not authenticated');
      return;
    }

    console.log('Creating workspace with plan:', { workspaceName, selectedPlan });
    
    createWorkspaceMutation.mutate({
      name: workspaceName,
      plan_code: selectedPlan
    }, {
      onSuccess: (data) => {
        onSuccess(data.workspace.id);
        handleClose();
      }
    });
  };

  const handleClose = () => {
    setWorkspaceName('');
    setSelectedPlan('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader className="flex flex-row items-center justify-between">
          <DialogTitle>Create New Workspace</DialogTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="h-6 w-6 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </DialogHeader>

        <Card className="p-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="workspaceName">Workspace Name</Label>
              <Input
                id="workspaceName"
                type="text"
                placeholder="Enter workspace name"
                value={workspaceName}
                onChange={(e) => setWorkspaceName(e.target.value)}
                disabled={createWorkspaceMutation.isPending}
              />
            </div>

            <div className="space-y-2">
              <Label>Select Plan</Label>
              <Select value={selectedPlan} onValueChange={setSelectedPlan} disabled={plansLoading || createWorkspaceMutation.isPending}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a plan" />
                </SelectTrigger>
                <SelectContent>
                  {plansData?.plans?.map((plan) => (
                    <SelectItem key={plan.id} value={plan.code}>
                      {plan.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={handleClose}
                disabled={createWorkspaceMutation.isPending}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleCreate}
                disabled={createWorkspaceMutation.isPending || !workspaceName.trim() || !selectedPlan}
                className="flex-1"
              >
                {createWorkspaceMutation.isPending ? 'Creating...' : 'Create Workspace'}
              </Button>
            </div>
          </div>
        </Card>
      </DialogContent>
    </Dialog>
  );
}